import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { selectOnFocusRequest } from '@/features/legends/questions/select-on-focus-request'
import {
  Inspector,
  InspectorContent,
  InspectorHeader,
  InspectorSection,
} from '@/features/legends/components/Inspector'
import { ErrorTabItem } from '@/features/legends/components/TabLabel'
import {
  ExpandableList,
  ExpandableListItem,
  ExpandableListItemContent,
  ExpandableListItemPanel,
} from '@/features/legends/questions/components/ExpandableList'
import { commitOnEnter } from '@/features/legends/editor/commit-on-enter'
import { firstError } from '@/features/legends/editor/validation'
import { EffectEditorContainer } from '@/features/legends/effects/components/EffectEditor/EffectEditorContainer'
import { FiltersBuilderContainer } from '@/features/legends/filters/components/FiltersBuilder/FiltersBuilderContainer'
import type {
  PropertyInspectorTab,
  PropertyInspectorViewModel,
} from '@/features/legends/questions/PropertyInspectorViewModel'
import type { AnyQuestionEditor } from '@/features/legends/questions/question-registry'
import {
  PropertyInspectorNote,
  PropertyInspectorSectionTitle,
  PropertyInspectorTitle,
} from '@/features/legends/questions/components/PropertyInspector'
import type { DocumentsQuestionEditorViewModel } from '@/features/legends/questions/types/DocumentsQuestionEditorViewModel'
import { QuestionGeneralEditorContainer } from '@/features/legends/questions/components/QuestionGeneralEditor/QuestionGeneralEditorContainer'

const testId = 'legend-questions-inspector'

const tabs: { tab: PropertyInspectorTab; title: string }[] = [
  { tab: 'General', title: 'General' },
  { tab: 'Conditions', title: 'Conditions' },
  { tab: 'Effects', title: 'Effects' },
]

export interface PropertyInspectorContainerProps {
  vm: PropertyInspectorViewModel
}

/**
 * Edits the selected question: its ID, fields, conditions and effects.
 */
export const PropertyInspectorContainer = observer(
  function PropertyInspectorContainer({ vm }: PropertyInspectorContainerProps) {
    const questionVm = vm.questionVm
    if (!questionVm) {
      return (
        <Inspector testId={testId}>
          <EmptyState size="sm" grow>
            <EmptyStateIconDecoration icon="edit" />
            <EmptyStateTitle>Nothing selected</EmptyStateTitle>
            <EmptyStateDescription>
              Select a question to edit its properties.
            </EmptyStateDescription>
          </EmptyState>
        </Inspector>
      )
    }
    if (questionVm.type === 'Documents') {
      return (
        <Inspector testId={testId}>
          <InspectorHeader
            title={
              <PropertyInspectorTitle
                type={questionVm.type}
                friendlyType={questionVm.info.friendlyType}
              >
                {questionVm.id}
              </PropertyInspectorTitle>
            }
          />
          <InspectorContent>
            <InspectorSection>
              <PropertyInspectorNote>
                Documents questions are deprecated and can't be edited. You can
                still move or remove this one.
              </PropertyInspectorNote>
            </InspectorSection>
          </InspectorContent>
        </Inspector>
      )
    }
    return (
      <QuestionInspectorContainer
        key={questionVm.cid}
        vm={vm}
        questionVm={questionVm}
      />
    )
  },
)

interface QuestionInspectorContainerProps {
  vm: PropertyInspectorViewModel
  questionVm: Exclude<AnyQuestionEditor, DocumentsQuestionEditorViewModel>
}

const QuestionInspectorContainer = observer(
  function QuestionInspectorContainer({
    vm,
    questionVm,
  }: QuestionInspectorContainerProps) {
    const idFieldFocus$ = useMemo(
      () => selectOnFocusRequest(questionVm.idFieldFocusRequest$),
      [questionVm],
    )
    return (
      <Inspector testId={testId}>
        <InspectorHeader
          title={
            <PropertyInspectorTitle
              type={questionVm.type}
              friendlyType={questionVm.info.friendlyType}
            >
              {!questionVm.info.optionalId && (
                <TextInputContainer
                  model={questionVm}
                  field="idDraft"
                  onBlur={questionVm.commitId}
                  onKeyDown={commitOnEnter(questionVm.commitId)}
                  error={questionVm.validation.getError('id')}
                  placeholder="Enter a question ID here"
                  isDisabled={vm.isEditingDisabled}
                  focus$={idFieldFocus$}
                  aria-label="Question ID"
                  data-test={`${testId}-id-input`}
                />
              )}
            </PropertyInspectorTitle>
          }
          tabs={tabs.map(({ tab, title }) => (
            <ErrorTabItem
              key={tab}
              title={title}
              error={vm.getTabError(tab)}
              isActive={vm.currentTab === tab}
              badgeCount={
                tab === 'Conditions' ? questionVm.conditionCount : undefined
              }
              onPress={() => vm.setTab(tab)}
              testId={`${testId}-${tab.toLowerCase()}`}
            />
          ))}
        />
        <InspectorContent>
          {vm.currentTab === 'General' && (
            <InspectorSection>
              <QuestionGeneralEditorContainer
                vm={questionVm}
                isDisabled={vm.isEditingDisabled}
                testId={`${testId}-general`}
              />
            </InspectorSection>
          )}
          {vm.currentTab === 'Conditions' && (
            <ConditionsContainer vm={vm} questionVm={questionVm} />
          )}
          {vm.currentTab === 'Effects' && (
            <EffectsContainer questionVm={questionVm} />
          )}
        </InspectorContent>
      </Inspector>
    )
  },
)

const ConditionsContainer = observer(function ConditionsContainer({
  vm,
  questionVm,
}: QuestionInspectorContainerProps) {
  const descriptors = questionVm.triggerWhenDescriptors
  return (
    <>
      <InspectorSection>
        <FiltersBuilderContainer
          vm={questionVm.triggerWhen}
          isDisabled={vm.isEditingDisabled}
          emptyText="No conditions configured; this element is always triggered."
          testId={`${testId}-conditions`}
        />
      </InspectorSection>
      {descriptors && descriptors.length > 0 && (
        <InspectorSection title="Option conditions" isFlush>
          <ExpandableList aria-label="Option conditions">
            {descriptors.map((descriptor) => (
              <ExpandableListItem
                key={descriptor.id}
                isExpanded={descriptor.triggerWhen.isExpanded}
                onExpandedChange={descriptor.triggerWhen.setExpanded}
                testId={`${testId}-option-conditions-${descriptor.id}`}
              >
                <ExpandableListItemContent
                  testId={`${testId}-option-conditions-${descriptor.id}-toggle`}
                >
                  <PropertyInspectorSectionTitle
                    title={descriptor.title}
                    error={firstError(descriptor.triggerWhen.validation)}
                  />
                </ExpandableListItemContent>
                <ExpandableListItemPanel>
                  <FiltersBuilderContainer
                    vm={descriptor.triggerWhen}
                    isDisabled={vm.isEditingDisabled}
                    emptyText="No conditions configured; this option is always available."
                    testId={`${testId}-option-conditions-${descriptor.id}-filters`}
                  />
                </ExpandableListItemPanel>
              </ExpandableListItem>
            ))}
          </ExpandableList>
        </InspectorSection>
      )}
    </>
  )
})

const EffectsContainer = observer(function EffectsContainer({
  questionVm,
}: Pick<QuestionInspectorContainerProps, 'questionVm'>) {
  const descriptors = questionVm.effectDescriptors
  if (!descriptors || descriptors.length === 0) {
    return (
      <InspectorSection>
        <PropertyInspectorNote>
          There are no effects to configure.
        </PropertyInspectorNote>
      </InspectorSection>
    )
  }
  return (
    <>
      <InspectorSection>
        <PropertyInspectorNote>
          You can configure things to happen when this question is answered a
          certain way.
        </PropertyInspectorNote>
      </InspectorSection>
      <ExpandableList aria-label="Effects">
        {descriptors.map((descriptor) => (
          <ExpandableListItem
            key={descriptor.id}
            isExpanded={descriptor.effect.isExpanded}
            onExpandedChange={descriptor.effect.setExpanded}
            testId={`${testId}-effect-${descriptor.id}`}
          >
            <ExpandableListItemContent
              testId={`${testId}-effect-${descriptor.id}-toggle`}
            >
              <PropertyInspectorSectionTitle
                title={descriptor.title}
                error={firstError(descriptor.effect.validation)}
              />
            </ExpandableListItemContent>
            <ExpandableListItemPanel>
              <EffectEditorContainer
                vm={descriptor.effect}
                descriptorId={descriptor.id}
                testId={`${testId}-effect-${descriptor.id}-mutators`}
              />
            </ExpandableListItemPanel>
          </ExpandableListItem>
        ))}
      </ExpandableList>
    </>
  )
})
